function formatConversionRate(rate) {
  const num = parseFloat(rate);
  if (Number.isNaN(num)) return '';
  return Number.isInteger(num) ? String(num) : String(parseFloat(num.toFixed(6)));
}

function getProductConversionsList(product) {
  if (!product) return [];
  if (product.conversions?.length) return product.conversions;
  if (product.unit_conversions?.length) return product.unit_conversions;
  return [];
}

function findProductConversion(product, unitCode, units = []) {
  const conversions = getProductConversionsList(product);
  if (!conversions.length || !unitCode) return null;

  const inputKey = String(unitCode).toLowerCase();
  const inputUnit = units.length ? findUnitInCatalog(unitCode, units) : null;

  const matchesFromUnit = (from) => {
    if (!from?.code) return false;
    if (String(from.code).toLowerCase() === inputKey) return true;
    if (inputUnit?.id && from.id && Number(from.id) === Number(inputUnit.id)) return true;
    if (!inputUnit || !units.length) return false;

    const fromCatalog = findUnitInCatalog(from.code, units);
    if (fromCatalog?.id && Number(fromCatalog.id) === Number(inputUnit.id)) return true;

    const inputName = String(inputUnit.name || '').trim().toLowerCase();
    const fromName = String(fromCatalog?.name || from.name || '').trim().toLowerCase();
    return Boolean(inputName && fromName && inputName === fromName);
  };

  return conversions.find((c) => c.is_active !== false && matchesFromUnit(c.from_unit)) || null;
}

/** Indica si dos códigos de unidad apuntan a la misma unidad del catálogo. */
export function unitsEquivalent(unitCodeA, unitCodeB, units = []) {
  if (!unitCodeA || !unitCodeB) return false;
  if (String(unitCodeA).toLowerCase() === String(unitCodeB).toLowerCase()) return true;
  if (!units.length) return false;

  const unitA = findUnitInCatalog(unitCodeA, units);
  const unitB = findUnitInCatalog(unitCodeB, units);
  if (unitA?.id && unitB?.id && Number(unitA.id) === Number(unitB.id)) return true;

  const nameA = String(unitA?.name || '').trim().toLowerCase();
  const nameB = String(unitB?.name || '').trim().toLowerCase();
  return Boolean(nameA && nameB && nameA === nameB);
}

export function getUnitFormulaForProduct(product, unitCode, units = []) {
  if (!product?.conversions || !unitCode) return '';
  const conv = findProductConversion(product, unitCode, units);
  if (!conv) return '';
  const toCode = conv.to_unit?.code || conv.to_unit?.symbol || product.unit_of_measure || '';
  const rate = formatConversionRate(conv.conversion_rate);
  return rate && toCode ? `1 ${conv.from_unit.code} = ${rate} ${toCode}` : '';
}

/**
 * Resuelve unidad desde Excel u otro texto (catálogo general o tasa personalizada del producto).
 */
export function resolveProductUnit({
  units = [],
  product = null,
  rawUnit = '',
  productConversionsEnabled = false,
}) {
  const defaultUom = product?.unit_of_measure || '';
  const trimmed = String(rawUnit || '').trim();
  if (!trimmed) {
    return { ok: true, code: defaultUom, formula: '', isCustom: false };
  }
  const lower = trimmed.toLowerCase();

  if (productConversionsEnabled && getProductConversionsList(product).length) {
    const conv = getProductConversionsList(product).find(
      (c) => c.is_active !== false && c.from_unit?.code?.toLowerCase() === lower,
    );
    if (conv?.from_unit?.code) {
      const formula = getUnitFormulaForProduct(product, conv.from_unit.code);
      return { ok: true, code: conv.from_unit.code, formula, isCustom: true };
    }
  }

  const unitRow = units.find((u) => u.code && String(u.code).toLowerCase() === lower);
  if (unitRow) {
    return { ok: true, code: unitRow.code, formula: '', isCustom: false };
  }

  const hint = productConversionsEnabled
    ? 'use el código del catálogo o una tasa personalizada configurada en el producto'
    : 'use el código exacto del catálogo de unidades';

  return {
    ok: false,
    error: `unidad «${trimmed}» no reconocida. Deje la celda vacía para usar la unidad del producto o ${hint}.`,
  };
}

/**
 * Arma grupos de unidades para selects con secciones:
 * - Tasa personalizada (conversiones del producto)
 * - Unidad general (catálogo)
 */
export function buildUnitSelectGroups({ units = [], product = null, productConversionsEnabled = false }) {
  const generalUnits = (units || []).map((unit) => ({
    code: unit.code,
    name: unit.name,
    symbol: unit.symbol || unit.code,
    label: unit.name ? `${unit.code} - ${unit.name}` : unit.code,
  }));

  if (!productConversionsEnabled) {
    return { showGroups: false, customUnits: [], generalUnits };
  }

  const conversions = getProductConversionsList(product).filter((c) => c.is_active !== false);
  const customCodes = new Set();
  const customUnits = [];

  conversions.forEach((conversion) => {
    const from = conversion.from_unit;
    if (!from?.code || customCodes.has(from.code)) return;
    customCodes.add(from.code);

    const toCode =
      conversion.to_unit?.code ||
      conversion.to_unit?.symbol ||
      product?.unit_of_measure ||
      '';
    const rate = formatConversionRate(conversion.conversion_rate);
    const formula = rate && toCode ? `1 ${from.code} = ${rate} ${toCode}` : '';

    customUnits.push({
      code: from.code,
      name: from.name,
      symbol: from.symbol || from.code,
      formula,
      label: formula ? `${from.code} — ${formula}` : from.code,
      conversion_rate: rate,
    });
  });

  const filteredGeneral = generalUnits.filter((unit) => !customCodes.has(unit.code));

  return {
    showGroups: true,
    customUnits,
    generalUnits: filteredGeneral,
  };
}

/**
 * Grupos de unidades por ID (p. ej. modal SUNAT que guarda unit_id).
 */
export function buildUnitIdSelectGroups({ units = [], product = null, productConversionsEnabled = false }) {
  const codeGroups = buildUnitSelectGroups({ units, product, productConversionsEnabled });
  const unitByCode = new Map(
    (units || [])
      .filter((u) => u.code)
      .map((u) => [String(u.code).toLowerCase(), u]),
  );

  const toIdOption = (entry) => {
    const unit = unitByCode.get(String(entry.code).toLowerCase());
    if (!unit?.id) return null;
    return {
      id: unit.id,
      code: entry.code,
      label: entry.label || entry.code,
    };
  };

  if (!codeGroups.showGroups) {
    return {
      showGroups: false,
      customUnits: [],
      generalUnits: (units || []).map((u) => ({
        id: u.id,
        code: u.code,
        label: u.name ? `${u.code} — ${u.name}` : u.code,
      })),
    };
  }

  return {
    showGroups: true,
    customUnits: codeGroups.customUnits.map(toIdOption).filter(Boolean),
    generalUnits: codeGroups.generalUnits.map(toIdOption).filter(Boolean),
  };
}

export function findUnitIdByCode(unitCode, units = []) {
  if (!unitCode || !units?.length) return null;
  const raw = String(unitCode).trim();
  const lower = raw.toLowerCase();
  const hit = units.find((u) => {
    const code = String(u.code ?? '').trim();
    const symbol = String(u.symbol ?? '').trim();
    const name = String(u.name ?? '').trim();
    return (
      code.toLowerCase() === lower ||
      symbol.toLowerCase() === lower ||
      name.toLowerCase() === lower ||
      (lower === 'unidad' && (code.toUpperCase() === 'UN' || name.toLowerCase() === 'unidad'))
    );
  });
  return hit?.id ?? null;
}

/** Busca unidad en catálogo por code, symbol o name (insensible a mayúsculas). */
export function findUnitInCatalog(raw, units = []) {
  if (!units?.length) return null;
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  return (
    units.find((u) => {
      const code = String(u.code ?? '').trim();
      const symbol = String(u.symbol ?? '').trim();
      const name = String(u.name ?? '').trim();
      return (
        (code && code.toLowerCase() === lower) ||
        (symbol && symbol.toLowerCase() === lower) ||
        (name && name.toLowerCase() === lower) ||
        (lower === 'unidad' && (code.toUpperCase() === 'UN' || name.toLowerCase() === 'unidad'))
      );
    }) || null
  );
}

const RECIPE_UNIT_LEGACY_ALIASES = {
  gramo: 'g',
  kilogramo: 'kg',
  litro: 'l',
  mililitro: 'ml',
  unidad: 'u',
};

/** Símbolo para mostrar en recetas (G / gramo → g). */
export function resolveRecipeIngredientUnitDisplay(raw, units = []) {
  const hit = findUnitInCatalog(raw, units);
  if (hit) return hit.symbol || hit.code || hit.name || '';
  const s = String(raw ?? '').trim();
  if (!s) return '';
  return RECIPE_UNIT_LEGACY_ALIASES[s.toLowerCase()] || s;
}

/** Código canónico del catálogo para guardar (g / gramo → G). */
export function resolveRecipeIngredientUnitCode(raw, units = []) {
  const hit = findUnitInCatalog(raw, units);
  if (hit?.code) return hit.code;
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const aliasSymbol = RECIPE_UNIT_LEGACY_ALIASES[s.toLowerCase()];
  if (aliasSymbol) {
    const hitAlias = findUnitInCatalog(aliasSymbol, units);
    if (hitAlias?.code) return hitAlias.code;
  }
  return s;
}

/**
 * Equivalente en unidad destino cuando la cantidad está en una tasa personalizada.
 * Ej.: 10 UNI con 1 UNI = 12 KG → "= 120 KG"
 */
export function getQuantityConversionHint({
  product,
  unitCode,
  quantity,
  productConversionsEnabled = false,
  units = [],
}) {
  if (!productConversionsEnabled) return null;

  const conv = findProductConversion(product, unitCode, units);
  if (!conv) return null;

  const qty = parseFloat(quantity);
  const rate = parseFloat(conv.conversion_rate);
  if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(rate) || rate <= 0) return null;

  const toCode =
    conv.to_unit?.code || conv.to_unit?.symbol || product?.unit_of_measure || '';
  if (!toCode) return null;

  const converted = qty * rate;
  const formatted = Number.isInteger(converted)
    ? String(converted)
    : String(parseFloat(converted.toFixed(4)));

  return {
    text: `= ${formatted} ${toCode}`,
    convertedQuantity: converted,
    targetUnit: toCode,
    fromUnit: conv.from_unit?.code || unitCode,
  };
}

/**
 * Cantidad efectiva para subtotales: si hay tasa personalizada, usa el equivalente convertido (ej. KG).
 */
export function itemHasCustomConversion({
  product = null,
  unitCode = '',
  productConversionsEnabled = false,
  units = [],
}) {
  if (!productConversionsEnabled) return false;
  return !!findProductConversion(product, unitCode, units);
}

export function getEffectiveQuantityForSubtotal({
  product = null,
  unitCode = '',
  quantity = 0,
  productConversionsEnabled = false,
  useConvertedQuantityForSubtotal = true,
  units = [],
}) {
  if (!useConvertedQuantityForSubtotal) {
    const qty = parseFloat(quantity);
    return Number.isFinite(qty) ? qty : 0;
  }

  const hint = getQuantityConversionHint({
    product,
    unitCode,
    quantity,
    productConversionsEnabled,
    units,
  });
  if (hint?.convertedQuantity != null) {
    return hint.convertedQuantity;
  }
  const qty = parseFloat(quantity);
  return Number.isFinite(qty) ? qty : 0;
}

/** Etiqueta de cantidad para impresión/PDF (muestra equivalente en unidad destino si hay tasa). */
export function getItemQuantityDisplayForPrint({
  product = null,
  unitCode = '',
  quantity = 0,
  productConversionsEnabled = false,
}) {
  const hint = getQuantityConversionHint({
    product,
    unitCode,
    quantity,
    productConversionsEnabled,
  });
  if (hint?.convertedQuantity != null) {
    const formatted = Number.isInteger(hint.convertedQuantity)
      ? String(hint.convertedQuantity)
      : String(parseFloat(hint.convertedQuantity.toFixed(4)));
    return { quantityLabel: `${formatted} ${hint.targetUnit}` };
  }
  const qty = parseFloat(quantity);
  const label = Number.isFinite(qty)
    ? String(parseFloat(qty.toFixed(4)))
    : String(quantity ?? '');
  const unit = unitCode || product?.unit_of_measure || '';
  return { quantityLabel: unit ? `${label} ${unit}` : label };
}

/**
 * Impresión alineada al formulario:
 * - Cantidad, unidad y precio: tal cual los inputs (ej. 4 UNI, S/ 8.4746).
 * - Subtotal: con tasa personalizada, el mismo cálculo del form (= 40 KG × precio → 338.98).
 */
export function getItemPrintLineDisplay({
  item,
  productConversionsEnabled = false,
  useConvertedQuantityForSubtotal = true,
}) {
  const formatQty = (n) => {
    const num = parseFloat(n);
    if (!Number.isFinite(num)) return String(n ?? '');
    return String(parseFloat(num.toFixed(4)));
  };

  const product = item?.product ?? null;
  const unitCode = item?.unit_of_measure ?? '';
  const quantity = item?.quantity ?? 0;
  const unitPrice = item?.unit_price ?? 0;

  const hint = getQuantityConversionHint({
    product,
    unitCode,
    quantity,
    productConversionsEnabled,
  });

  let subtotal = item?.subtotal;
  if (hint) {
    const calculated = Math.round(
      getItemLineSubtotal({
        product,
        unitCode,
        quantity,
        unitPrice,
        productConversionsEnabled,
        useConvertedQuantityForSubtotal,
      }) * 100,
    ) / 100;
    const stored = parseFloat(item?.subtotal);
    subtotal =
      Number.isFinite(stored) && Math.abs(stored - calculated) < 0.01
        ? stored
        : calculated;
  }

  return {
    quantity: formatQty(quantity),
    unit: unitCode || product?.unit_of_measure || '-',
    subtotal,
  };
}

/**
 * Con medida convertida y tasa personalizada: cantidad/unidad visibles en unidad destino (ej. 10 KG).
 */
export function applyConvertedLineDisplay({
  product = null,
  unitCode = '',
  quantity = 0,
  productConversionsEnabled = false,
  useConvertedQuantityForSubtotal = true,
  units = [],
}) {
  const qty = parseFloat(quantity);
  const safeQty = Number.isFinite(qty) ? qty : 0;
  const baseUnit = unitCode || product?.unit_of_measure || '';

  if (!productConversionsEnabled || !useConvertedQuantityForSubtotal) {
    return {
      quantity: safeQty,
      unitCode: baseUnit,
      usesConvertedDisplay: false,
    };
  }

  const hint = getQuantityConversionHint({
    product,
    unitCode: baseUnit,
    quantity: safeQty,
    productConversionsEnabled,
    units,
  });

  if (hint?.convertedQuantity == null || !hint?.targetUnit) {
    return {
      quantity: safeQty,
      unitCode: baseUnit,
      usesConvertedDisplay: false,
    };
  }

  const converted = parseFloat(hint.convertedQuantity);
  return {
    quantity: Number.isFinite(converted) ? converted : safeQty,
    unitCode: hint.targetUnit,
    usesConvertedDisplay: true,
  };
}

/** Subtotal de línea: cantidad efectiva (convertida si aplica) × precio unitario neto. */
export function getItemLineSubtotal({
  product = null,
  unitCode = '',
  quantity = 0,
  unitPrice = 0,
  productConversionsEnabled = false,
  useConvertedQuantityForSubtotal = true,
  units = [],
}) {
  const effectiveQty = getEffectiveQuantityForSubtotal({
    product,
    unitCode,
    quantity,
    productConversionsEnabled,
    useConvertedQuantityForSubtotal,
    units,
  });
  const price = parseFloat(unitPrice);
  return effectiveQty * (Number.isFinite(price) ? price : 0);
}

export function resolveUnitIdForProduct({
  units = [],
  product = null,
  rawUnit = '',
  productConversionsEnabled = false,
}) {
  const resolved = resolveProductUnit({
    units,
    product,
    rawUnit,
    productConversionsEnabled,
  });
  if (!resolved.ok) return null;
  return findUnitIdByCode(resolved.code, units);
}
